import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';
import { Form, Formik } from 'formik';
import { Theme } from '~/themes';
import { ORGANIZATION_DEFAULT } from '~/constants/adminOptions';
import { alertParams, showAlert } from '~/components/common/Alert';
import { IOrg } from '~/models/org';
import { useUpdateOrgMutation } from '~/services/api/org';
import { OrgSchema } from '~/utils/schema/org';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { FastField, Field } from '~/components/common/Formik';
import { ORG_CATEGORY_OPTIONS } from '~/constants/common';
import { useAuth } from '~/providers/AuthProvider';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import InfoIcon from '~/assets/images/icons/info-black.svg';
import ActionBar from '../components/ActionBar';

const style = (theme: Theme) =>
  createStyles({
    container: {
      height: '100%',
      maxWidth: '824px',
      width: '100%',
      position: 'relative',
      color: theme.color.violet500,
      margin: 'auto',
    },
    content: {
      backgroundColor: 'white',
      borderRadius: theme.spacing(0.8),
      border: '1px solid #E2E7F0',
      padding: theme.spacing(2.4),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1.6),
      },
    },
    title: {
      color: theme.color.violet500,
      fontSize: 18,
      fontWeight: 500,
    },
  });

interface IProps extends WithStyles<typeof style> {}

const Organization: React.FC<IProps> = ({ classes }) => {
  const { account } = useAuth();
  const { org, isBundlePlan } = usePaymentInfo();

  const { mutate: updateOrg, isLoading: loadingOrgUpdate } =
    useUpdateOrgMutation({
      onSuccess: () => {
        showAlert(alertParams.success);
      },
    });

  const onSubmit = (values: IOrg) => {
    updateOrg({
      name: values.name,
      category: values.category,
      auto_assign: values.auto_assign,
    });
  };

  return (
    <Formik
      initialValues={account?.org || ORGANIZATION_DEFAULT}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={OrgSchema}
    >
      {({ isValid, dirty, submitForm, resetForm }) => (
        <Form
          style={{
            height: '100%',
            width: '100%',
          }}
        >
          <Stack className={classes.container}>
            <Stack className={classes.content}>
              <Grid container rowSpacing={1.6}>
                <Grid item xs={12}>
                  <Typography className={classes.title}>
                    General Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Field.TextInput
                    name='name'
                    legend='Company Name'
                    disabled={Boolean(org?.bs_org_id && isBundlePlan)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field.Select
                    name='category'
                    legend='Business Category'
                    options={ORG_CATEGORY_OPTIONS}
                    sortOption
                  />
                </Grid>
              </Grid>
            </Stack>
            <Stack sx={{ mt: 1.6 }} className={classes.content}>
              <Grid container rowSpacing={1.6}>
                <Grid item xs={12}>
                  <Typography className={classes.title}>
                    Auto-Assignment
                  </Typography>
                  <Stack flexDirection='row' alignItems='center' mt={1.6}>
                    <FastField.SwitchButton noText name='auto_assign' />
                    <Typography>
                      Automatically assign incoming delivery tasks to drivers
                      based on capacity and priority.
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <img src={InfoIcon} alt='info' />
                    <Typography>
                      Once a task is manually managed in any way,
                      auto-assignment will no longer be applied to that specific
                      task.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </Stack>
          {dirty && (
            <ActionBar
              onSave={submitForm}
              onCancel={() => resetForm()}
              loading={loadingOrgUpdate}
              disabled={!isValid || !dirty}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default withStyles(style)(Organization);
